import { useEffect, useState } from 'react';
import { useWindowSize } from 'react-use';

interface UseMinMaxContainerHeightOptions {
  minHeight?: number;
  maxHeight?: number;
  windowMarginPixels?: number;
}

/**
 * React Hook to manage the min/max height of a container based on window size.
 * @param ref - The ref of the container element.
 * @param param1 - Configuration options for min/max height.
 * @returns The current height of the container.
 */
export function useMinMaxContainerHeight(
  ref: React.RefObject<HTMLElement>,
  {
    minHeight,
    maxHeight,
    windowMarginPixels = 10,
  }: UseMinMaxContainerHeightOptions,
) {
  const { height: windowHeight } = useWindowSize();
  const [height, setHeight] = useState(0);

  const top = ref.current?.getBoundingClientRect().top ?? 0;

  useEffect(() => {
    if (ref.current) {
      let newHeight = windowHeight - windowMarginPixels - top;

      if (minHeight !== undefined) {
        newHeight = Math.max(newHeight, minHeight);
      }

      if (maxHeight !== undefined) {
        newHeight = Math.min(newHeight, maxHeight);
      }

      setHeight(newHeight);
    }
  }, [windowHeight, windowMarginPixels, top, ref, maxHeight, minHeight]);

  return {
    height,
  };
}
